import React, { Component, ErrorInfo, ReactNode } from 'react';

/**
 * The app had no error boundary of any kind, so a render-time exception anywhere in the tree
 * unmounted React's whole root and left a blank white page with no way back except a manual
 * reload — the failure mode is silent, and the user is simply stuck.
 *
 * This catches render/lifecycle errors below it and offers a way out. It deliberately does NOT
 * catch errors thrown in event handlers or async callbacks (React boundaries never do) — those
 * still belong in the local try/catch each caller already has.
 */
interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Kept to the console rather than sent anywhere: this project ships no error-reporting
        // service, and inventing one here would be a silent new outbound dependency.
        console.error('Unhandled render error:', error, errorInfo.componentStack);
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div
                role="alert"
                style={{
                    minHeight: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                    padding: 24,
                    textAlign: 'center',
                }}
            >
                <h2 style={{ margin: 0 }}>Niečo sa pokazilo</h2>
                <p style={{ margin: 0, opacity: 0.8 }}>
                    Stránku sa nepodarilo zobraziť. Skúste to prosím znova.
                </p>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    Obnoviť stránku
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;
